import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '../../../components/ui';

// Irish counties organized by province
const COUNTIES = {
  Dublin: [
    'Dublin 1', 'Dublin 2', 'Dublin 3', 'Dublin 4', 'Dublin 5',
    'Dublin 6', 'Dublin 6W', 'Dublin 7', 'Dublin 8', 'Dublin 9',
    'Dublin 10', 'Dublin 11', 'Dublin 12', 'Dublin 13', 'Dublin 14',
    'Dublin 15', 'Dublin 16', 'Dublin 17', 'Dublin 18', 'Dublin 20',
    'Dublin 22', 'Dublin 24',
    'Dún Laoghaire', 'Swords', 'Malahide', 'Howth', 'Bray',
    'Blackrock', 'Sandyford', 'Tallaght', 'Blanchardstown', 'Clondalkin',
  ],
  Leinster: [
    'Carlow', 'Kildare', 'Kilkenny', 'Laois', 'Longford',
    'Louth', 'Meath', 'Offaly', 'Westmeath', 'Wexford', 'Wicklow',
  ],
  Munster: [
    'Clare', 'Cork', 'Kerry', 'Limerick', 'Tipperary', 'Waterford',
  ],
  Connacht: [
    'Galway', 'Leitrim', 'Mayo', 'Roscommon', 'Sligo',
  ],
  Ulster: [
    'Cavan', 'Donegal', 'Monaghan',
  ],
};

// Flatten for select options
const ALL_AREAS = [
  { group: 'Dublin', options: COUNTIES.Dublin },
  { group: 'Leinster', options: COUNTIES.Leinster },
  { group: 'Munster', options: COUNTIES.Munster },
  { group: 'Connacht', options: COUNTIES.Connacht },
  { group: 'Ulster (ROI)', options: COUNTIES.Ulster },
];

/**
 * Step 5: Location
 *
 * Simplified county picker + private address.
 */
export default function StepLocation({ onNext, onBack }) {
  const {
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();

  const pickupArea = watch('pickupArea');
  const pickupAddress = watch('pickupAddress');

  const isValid = pickupArea?.length >= 2 && pickupAddress?.length >= 10;

  const handleAddressChange = useCallback((e) => {
    setValue('pickupAddress', e.target.value, { shouldValidate: true });
  }, [setValue]);

  async function handleContinue() {
    const valid = await trigger(['pickupArea', 'pickupAddress']);
    if (valid) {
      onNext();
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-display text-cream mb-2">
          Pickup Location
        </h1>
        <p className="text-cream/60">
          Where will the winner collect the item?
        </p>
      </div>

      {/* Form */}
      <div className="space-y-8 mb-8">
        {/* Section 1: Public Area */}
        <div className="bg-gradient-to-br from-primary/5 via-midnight-light to-accent/5 border border-primary/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-cream">Public Area</h2>
              <p className="text-cream/50 text-sm">Shown to all buyers before they enter</p>
            </div>
          </div>

          {/* County/Area Select */}
          <div>
            <label className="block text-xs font-medium text-cream/70 uppercase tracking-wider mb-2">
              County / Area
            </label>
            <select
              value={pickupArea || ''}
              onChange={(e) => setValue('pickupArea', e.target.value, { shouldValidate: true })}
              className="w-full bg-midnight border border-primary/30 rounded-lg px-4 py-3 text-cream appearance-none cursor-pointer focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
            >
              <option value="">Select your area...</option>
              {ALL_AREAS.map(({ group, options }) => (
                <optgroup key={group} label={group}>
                  {options.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {errors.pickupArea?.message && (
              <p className="text-error text-sm mt-1">{errors.pickupArea.message}</p>
            )}
          </div>

          {/* Selected display */}
          {pickupArea && (
            <div className="mt-4 bg-midnight border border-primary/30 rounded-lg px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-lg font-semibold text-primary">{pickupArea}</span>
              </div>
              <button
                type="button"
                onClick={() => setValue('pickupArea', '', { shouldValidate: true })}
                className="text-cream/40 hover:text-cream transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Section 2: Private Address */}
        <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-cream">Private Address</h2>
              <p className="text-cream/50 text-sm">Only revealed to the winner after the draw</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-cream/70 uppercase tracking-wider mb-2">
              Full Pickup Address
            </label>
            <textarea
              placeholder="123 Main Street&#10;Apartment 4B&#10;Dublin 8"
              rows={4}
              maxLength={200}
              value={pickupAddress || ''}
              onChange={handleAddressChange}
              className="w-full bg-midnight border border-primary/30 rounded-lg px-4 py-3 text-cream placeholder:text-cream/30 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-cream/40 text-xs">
                {(pickupAddress?.length || 0)} / 200 characters
              </span>
            </div>
            {errors.pickupAddress?.message && (
              <p className="text-error text-sm mt-1">{errors.pickupAddress.message}</p>
            )}
          </div>
        </div>

        {/* Privacy Info */}
        <div className="bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20 rounded-xl p-5">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-cream mb-2">Your Privacy is Protected</h3>
              <ul className="space-y-1.5 text-cream/60 text-sm">
                <li className="flex items-center gap-2">
                  <span className="text-accent">✓</span>
                  Only the general area is shown publicly
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-accent">✓</span>
                  Full address hidden until winner is drawn
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-accent">✓</span>
                  Only verified winners can see your address
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleContinue} disabled={!isValid}>
          Review Competition
        </Button>
      </div>
    </div>
  );
}
